import { __decorate } from "tslib";
import { Vue, Component } from "vue-property-decorator";
import { Getter, Action } from "vuex-class";
// @ts-ignore
import { version as appVersion } from "../package.json";
import * as _ from "lodash";
import moment from "moment";
import { ConversationType, } from "@/helpers/types";
import { noticeCodes } from "./helpers/genesys";
import { _generateUUID, isIncomingMessage, isMamMessage, mainThread, debounce, getUrlQueryParams, } from "./helpers/functions";
import strings from "./static/strings";
import { welcomeMessagePayload, restartMessagePayload, timeoutMessagePayload, carouselMessageSingleChoiceImageText, } from "./static/mocks";
import { adamActionsConfig } from "./helpers/adam";
import WebChatCore from "@monksoftware/webchat-core-wfp/dist/MonkWebChatCoreWfp.esm";
import WebChatAuthenticator from "./authenticator/index";
import CallToAction from "@/components/CallToAction.vue";
Vue.use(WebChatAuthenticator, {
    context: "wfp",
    basePath: process.env.VUE_APP_API_ENDPOINT
});
// Add and override MonkChatCore methods
const WfpChat = WebChatCore.extend({
    methods: {
        /**
         * @returns {VueInstance} The instance of App component.
         */
        getInstance() {
            return this.$parent;
        },
        /**
         * @returns {VueInstance} Returns the instance of Webchat component reference.
         */
        getWebchatInstance() {
            return this.getInstance().$refs.webchat;
        },
        /**
         * If "key" is in localStorage this method returns its decrypted value.
         * @param {string} key - The localStorage key
         * @returns {(string|null)} Decrypted value of key otherwise null
         */
        _slsGet(key) {
            return this.getInstance()._slsGet(key);
        },
        /**
         * Set (encrypted) value into localStorage
         * @param {string} key - The localStorage key
         * @param {any} value - The corresponding value
         * @returns {void}
         */
        _slsSet(key, value) {
            return this.getWebchatInstance().slsSet(key, value);
        },
        /**
         * Returns the current conversation thread or boolean
         * @returns {(string|false)}
         */
        getCurrentConversationThread() {
            if (this.getInstance().canShowConversationsView) {
                return false;
            }
            const { class: currentChatClass, thread } = this.getWebchatInstance().currentChat;
            if (currentChatClass && thread) {
                const isWfpChat = currentChatClass === ConversationType.Wfp;
                return !isWfpChat ? thread : this.getInstance().getMainThread();
            }
            else {
                return this.getInstance().getMainThread();
            }
        },
        getMessageSender(msg = {}) {
            let sender = "user";
            const { payload } = msg;
            if (!payload)
                return sender;
            const { data } = payload;
            if (!data)
                return sender;
            const { conversationType } = data;
            return conversationType || sender;
        },
        getConstructedStoreMessage(msg = {}) {
            // Check for current conversation thread
            const keyThread = this.isMessageHasStanza(msg) && msg.stanza.thread !== "anonymous"
                ? msg.stanza.thread
                : this.getCurrentConversationThread();
            // Check for message sender
            const sender = this.getMessageSender(msg);
            msg = Object.assign({}, this.getHandleConversationTypeSwitch(msg, sender));
            // Use a key to reference the message into history
            return Object.assign({}, msg, { key: keyThread });
        },
        /**
         * Update chat UI (title & header icon) based on incoming message
         * @param {any} message - Incoming message
         * @param {string} sender - Message sender (bot | operator)
         * @returns {void}
         */
        getHandleConversationTypeSwitch(message, sender) {
            let chatHeaderIcon = "";
            if (isIncomingMessage(message) &&
                message.stanza &&
                message.stanza.thread === this.getCurrentConversationThread()) {
                if (sender === "operator") {
                    const chatHeaderClass = "wfp";
                    document.getElementById("chat-header")?.classList.add(chatHeaderClass);
                    chatHeaderIcon = this.getInstance()._getRenderedAsset.avatar_operatore;
                }
                else if (sender === "bot") {
                    document.getElementById("chat-header")?.classList.add("wfp");
                    chatHeaderIcon = this.getInstance()._getRenderedAsset.ico_wfp;
                }
            }
            if (chatHeaderIcon) {
                this.getWebchatInstance().setChatHeaderIcon(chatHeaderIcon);
            }
            return this.getSingleMessagesAvatar(message, sender);
        },
        /**
         * Update message avatar based on incoming message
         * @param {any} message - Incoming message
         * @param {string} sender - Message sender (bot | operator)
         * @returns {void}
         */
        getSingleMessagesAvatar(message, sender) {
            const { type, avatar, stanza } = message;
            if (type === "chat_notice" || avatar) {
                return message;
            }
            const direction = stanza ? stanza.direction : "out";
            // default avatar
            let fromAvatar = this.getInstance()._getRenderedAsset.ico_user;
            if (direction === "in") {
                // wfp avatar
                if (sender === "operatore") {
                    fromAvatar = this.getInstance()._getRenderedAsset.ico_operatore; // operator
                }
                else {
                    fromAvatar = this.getInstance()._getRenderedAsset.avatar_wfp;
                }
            }
            return Object.assign({}, message, { fromAvatar });
        },
        /**
         * Toggle attachment icon
         * @param {boolean} canShow - A flag that tells if the attachments are enabled or not
         * @returns {void}
         */
        prepareAttachmentsInformations(canShow = true) {
            let config = this.getWebchatInstance().attachmentsUploaderConfig;
            if (canShow) {
                const { connection } = this.getWebchatInstance().instance;
                const JID = connection ? connection.authcid : false;
                if (JID) {
                    config = {
                        ...config,
                        requestData: {
                            user: `${JID}:${this.getCurrentConversationThread()}`
                        }
                    };
                }
            }
            this.getWebchatInstance().setAttachmentsUploaderConfig({
                ...config,
                use: canShow
            });
        },
        // this is a function wrapper
        // triggered inside core
        handleOnRestartChat() {
            this.getInstance().handleRestartMessage();
        },
        handleAdamActionMessageCodes(code) {
            switch (code) {
                case adamActionsConfig[0]: // conversation.chat.disable
                case adamActionsConfig[1]: // conversation.chat.enable
                    const isShowInput = code === adamActionsConfig[1];
                    // toggle chat input
                    if (this._webchatReference) {
                        this._webchatReference.toggleUserInputArea(isShowInput);
                        this._webchatReference.toggleUserInputStatus(isShowInput);
                    }
                    break;
                case adamActionsConfig[4]: // conversation.typing.stop
                case adamActionsConfig[5]: // conversation.typing.start
                    const isStartTyping = code === adamActionsConfig[5];
                    this.getInstance().handleShowIsTypingBubble(isStartTyping);
                    break;
                default:
                    break;
            }
        },
        handleIncomingMessageAdamActions(message) {
            const { payload } = message;
            if (payload) {
                const { adamActions } = payload;
                if (adamActions) {
                    adamActions.forEach(({ code }) => {
                        this.handleAdamActionMessageCodes(code);
                    });
                }
            }
        },
        handleIncomingMessageInputInteractions(msg) {
            // handle incoming location node
            if (this.isMessageInputLocation(msg)) {
                this.getWebchatInstance().setIsInteractionInsertLocation(true);
            }
            else {
                this.getWebchatInstance().setIsInteractionInsertLocation(false);
            }
            // handle incoming input date node
            if (this.isMessageInputDate(msg)) {
                const { payload } = msg;
                const { inputData } = payload;
                this.getWebchatInstance().setIsInteractionInsertDate(true);
                this.getWebchatInstance().setCurrentDatePickerInfo({
                    status: "input_date",
                    type: inputData.interaction.options.dateType || "",
                    options: inputData.interaction.options
                });
            }
            else {
                this.getWebchatInstance().setIsInteractionInsertDate(false);
                this.getWebchatInstance().setCurrentDatePickerInfo({});
            }
        },
        /**
         * Returns corrected message object needed for internal functions
         * @param {object} msg - the message object payload
         * @returns {object} message object with specific properties
         * i.e. { type, action, text }
         */
        getMessageChatTextPayload(msg = {}) {
            const { type, action, payload } = msg;
            if (type && action && payload) {
                const { text } = payload;
                return { type, action, text };
            }
            return { type, action, text: "" };
        },
        /**
         * Validate input message
         * @param {string} input - the string message from the user input field who interacts with the bot
         * @returns {boolean}
         */
        isMessageInputValidated(input) {
            const pattern = /^[^<>]+$/;
            return pattern.test(input);
        },
        isMessageInputLocation(message = {}) {
            const { payload } = message;
            if (!payload) {
                return false;
            }
            const { inputData } = payload;
            if (!inputData) {
                return false;
            }
            const { interaction } = inputData;
            return interaction && interaction.type === "insert_location";
        },
        isMessageInputDate(message = {}) {
            const { payload } = message;
            if (!payload) {
                return false;
            }
            const { inputData } = payload;
            if (!inputData) {
                return false;
            }
            const { interaction } = inputData;
            return interaction && interaction.type === "input_date";
        },
        isMessageContinueConversation(msg = {}) {
            const { payload } = msg;
            if (!payload) {
                return false;
            }
            const { adamActions } = payload;
            if (!adamActions) {
                return false;
            }
            const [adamItem] = adamActions;
            // code is frontend only reference, and only used by frontend web
            // declation source is from mocks.ts/timeoutMessagePayload
            return adamItem && adamItem.code === "conversation.session.continue_conversation";
        },
        isAdamActionCodeRestart(adamActions = []) {
            if (!adamActions) {
                return false;
            }
            const [adamItem] = adamActions;
            return adamItem && adamItem.code === "conversation.session.restart";
        },
        isMessageRestartNode(msg = {}) {
            const { payload } = msg;
            if (!payload) {
                return false;
            }
            const { text } = payload;
            const restartText = strings.defaultTimeoutRestartMessage.toLocaleLowerCase();
            if (text && text.toLocaleLowerCase() === restartText) {
                // if node is restart inside timeout message
                const { content } = payload;
                if (!content) {
                    return false;
                }
                const [contentItem] = content;
                if (!contentItem) {
                    return false;
                }
                const { adamActions } = contentItem;
                return this.isAdamActionCodeRestart(adamActions);
            }
            else {
                // if node is restart from standalone button
                const { adamActions } = payload;
                return this.isAdamActionCodeRestart(adamActions);
            }
        },
        isMessageFinalNode(msg = {}) {
            const { payload } = msg;
            if (!payload) {
                return false;
            }
            const { adamActions } = payload;
            if (!adamActions) {
                return false;
            }
            const [adamItem] = adamActions;
            return adamItem && adamItem.code === "conversation.status.end";
        },
        /**
         * Confirms the message is of type chatText
         * @param {object} msg - the message object payload
         * @returns {boolean}
         */
        isMessageChatText(msg = {}) {
            const { type, action, text } = this.getMessageChatTextPayload(msg);
            return type === "chat_text" && action === "chat_text" && !!text;
        },
        isMessageResponseIncoming(message) {
            const { stanza } = message;
            if (stanza) {
                const { direction } = stanza;
                return direction === "in";
            }
            else {
                return false;
            }
        },
        isLastConversationOutgoingMessage(currentConversation = []) {
            const lastItem = currentConversation.slice(-1);
            const [lastItemObj] = lastItem;
            if (!lastItemObj) {
                return false;
            }
            return !isIncomingMessage(lastItemObj);
        },
        isMessageAdamService(message) {
            const { type, action } = message;
            return type === "adam_service" && action === "adam_service";
        },
        isMessagePrivacyResponse(message) {
            let isPrivacy = false;
            if (message) {
                const { selectedChoices } = message.payload;
                if (selectedChoices && selectedChoices.length) {
                    const [choice] = selectedChoices;
                    const privacyChoices = ["privacy_ok", "privacy_ko"];
                    isPrivacy = privacyChoices.includes(choice);
                }
            }
            return isPrivacy;
        },
        isMessageHasStanza(msg) {
            if (!msg) {
                return false;
            }
            const { stanza } = msg;
            return !!stanza;
        },
        isMessageHasStanzaWithEqualThread(msg) {
            if (!msg) {
                return false;
            }
            const { stanza } = msg;
            return (this.isMessageHasStanza(msg) &&
                stanza.hasOwnProperty("thread") &&
                stanza.thread === this.getCurrentConversationThread());
        },
        isLastConversationPrivacyResponse(currentConversation = []) {
            const lastItem = currentConversation.slice(-1);
            const [lastItemObj] = lastItem;
            if (!lastItemObj) {
                return false;
            }
            return this.isMessagePrivacyResponse(lastItemObj);
        },
        isMessageOkToBeAddedToConversation(msg, currentMessage) {
            const isPrivacyMessage = this.isMessagePrivacyResponse(msg);
            const isMessageAdamService = this.isMessageAdamService(msg);
            const isMessageRestartNode = this.isMessageRestartNode(msg);
            const currentMamId = this.getWebchatInstance().getMamIdFromStanza(msg.stanza);
            const isSpocMessageAlreadyExists = this.getWebchatInstance().isMamMessageExistsOnConversation([...currentMessage], msg, currentMamId);
            return (!isPrivacyMessage &&
                !isMessageAdamService &&
                // from mam query, not a restart node
                ((!isSpocMessageAlreadyExists && !isMessageRestartNode) ||
                    // a restart node, but from mam query
                    (currentMamId && !isSpocMessageAlreadyExists && isMessageRestartNode) ||
                    // a restart node, from realtime chat reset
                    (!currentMamId && isMessageRestartNode)));
        },
        handleFireUnreadMessages(keyThread) {
            // just put this into a wrapper function
            // so from here we can add logics later on
            this.getWebchatInstance().handleUnreadMessages(keyThread);
        },
        handleFireResetUnreadMessages(keyThread, mode = "all", msg) {
            if (mode === "selective") {
                const mainThread = this.getInstance().getMainThread();
                const { currentChat } = this.getWebchatInstance();
                const isChatView = Object.entries(currentChat).length > 0;
                // handle for support conversations closed by the agent
                if (msg && msg.hasOwnProperty("payload")) {
                    if (msg.payload.hasOwnProperty("code")) {
                        // noticeCodes[1] is GENESYS#0002
                        // "This conversation has now been closed by the agent"
                        if (msg.payload.code === noticeCodes[1]) {
                            // reset unread counter
                            this.getWebchatInstance().resetUnreadMessagesCount(keyThread);
                            // TODO: confirm disable input area
                        }
                    }
                }
                if (isChatView) {
                    const { class: currentClass, thread: currentThread } = currentChat;
                    // if current thread is spoc
                    if (currentClass === ConversationType.Wfp) {
                        // console.log("reset spoc conversation...");
                        // reset unread messagges count
                        this.getWebchatInstance().resetUnreadMessagesCount(mainThread);
                    }
                    else {
                        // if current thread is a support conversation
                        if (currentThread === keyThread) {
                            // console.log("reset human conversation...");
                            this.getWebchatInstance().resetUnreadMessagesCount(currentThread);
                        }
                    }
                }
            }
            else {
                // reset unread messagges count
                this.getWebchatInstance().resetUnreadMessagesCount(keyThread);
            }
        },
        handleFireShowTypingBubble(msg) {
            const { class: currentChatClass } = this.getWebchatInstance().currentChat;
            const isWfp = currentChatClass === ConversationType.Wfp;
            const { getCurrentChatMessages } = this.getWebchatInstance();
            if (isWfp && this.isLastConversationOutgoingMessage(getCurrentChatMessages) && !this.isMessageRestartNode(msg)) {
                console.log("show is typing bubble...");
                // handle debounce show typing bubble
                // here, just add a debounce so it will trigger last
                // this.getInstance().debounceShowTypingBubble(true);
                this.getInstance().handleShowIsTypingBubble(true);
            }
        },
        getUpdatedCurrentConversationMessage(msg, conversationMessages = [], keyThread) {
            // create a shallow copy
            const currentConversationMessages = [...conversationMessages];
            const { isXmppQueryArchivePagination, shouldForceQueryArchivePagination, isCurrentConversationPaginationModeForward } = this.getWebchatInstance();
            if (this.isMessageResponseIncoming(msg) && !isXmppQueryArchivePagination) {
                // handle disable is typing bubble
                this.getInstance().handleShowIsTypingBubble(false);
                // handle adamActions for incoming message
                this.handleIncomingMessageAdamActions(msg);
                // handle incoming input interactions
                // i.e. detecting input location nodes, input date nodes
                this.handleIncomingMessageInputInteractions(msg);
            }
            const { getCurrentChatMessages } = this.getWebchatInstance();
            const isPrivacyMessage = this.isMessagePrivacyResponse(msg);
            const isLastMessagePrivacyResponse = this.isLastConversationPrivacyResponse(getCurrentChatMessages);
            const isMessageOkToBeAddedToConversation = this.isMessageOkToBeAddedToConversation(msg, [
                ...currentConversationMessages
            ]);
            if (isMessageOkToBeAddedToConversation) {
                console.log("message is ok to be added to conversation...");
                // for bot response(incoming)
                // and user response(outgoing)
                // but not user privacy response
                // and not adam_service response
                if (isXmppQueryArchivePagination || shouldForceQueryArchivePagination) {
                    // and if messages is from pagination
                    console.log("receiving mam with pagination message...");
                    // we have to wait all the previous messages to arrive first
                    // so lets stage the results of the pagination for the moment
                    // and only prepend to the currentConversationMessage when all messages arrives
                    // then the handleDisplayPreviousMessages() from the webchat-core-wfp module
                    // will handle the sorting and the realignment to local storage
                    this.getWebchatInstance().pushMessageInCurrentConversationPagination(msg);
                    // fire unread message
                    if (isCurrentConversationPaginationModeForward) {
                        this.handleFireUnreadMessages(keyThread);
                    }
                }
                else {
                    // default mam query
                    // and not messages from pagination
                    console.log("receiving mam message...");
                    // add message to current conversation
                    this.getWebchatInstance().pushMessageInCurrentConversation(msg);
                    // add to conversation message tree
                    currentConversationMessages.push(msg);
                    // Add message into messages array store
                    this.getWebchatInstance().addMessage(msg);
                    // Add message into conversation history
                    this.getWebchatInstance().addMessageToHistory(msg);
                    // handle input modes before/after
                    this.getInstance().handleInputModesBeforeAfter(msg);
                    // fire unread messages
                    this.handleFireUnreadMessages(keyThread);
                }
            }
            else if (isPrivacyMessage && !isLastMessagePrivacyResponse) {
                // if user privacy response(accept/reject)
                // we put a hardcheck to only store the response msg once
                // TODO: check if this is needed in the future, otherwise remove
                // no user privacy response for wfp atm
                // this.getWebchatInstance().pushMessageInCurrentConversation(msg);
            }
            return currentConversationMessages;
        },
        /**
         * Stores message on UI, in Vuex store and into localStorage (?)
         * @override MonkChatCore.storeMessage method
         * @param {any} msg - The message to store
         * @param {boolean} storeInLS - A flag that tells if msg has to be stored in localStorage or not
         * @returns {void}
         */
        storeMessage(msg, storeInLS = true) {
            // Retrieve messages
            const localMessagesTree = this.getInstance().getLocalMessagesDictionary();
            // Check for current conversation thread
            const keyThread = this.isMessageHasStanza(msg) && msg.stanza.thread !== "anonymous"
                ? msg.stanza.thread
                : this.getCurrentConversationThread();
            // console.log("mainThread: ", this.getInstance().getMainThread());
            // console.log("keyThread: ", keyThread);
            // Check if current messages array is already created or not
            if (!localMessagesTree.hasOwnProperty(keyThread)) {
                localMessagesTree[keyThread] = [];
            }
            // get constructed message
            msg = Object.assign({}, this.getConstructedStoreMessage(msg));
            const { isXmppQueryArchivePagination, isCurrentConversationPaginationModeForward } = this.getWebchatInstance();
            const canAddMessageToCurrentConversation = keyThread === this.getCurrentConversationThread();
            // console.log("canAddMessageToCurrentConversation: ", canAddMessageToCurrentConversation);
            // console.log("msg: ", JSON.stringify(msg));
            // Check if message can be added to currently open conversation
            if (canAddMessageToCurrentConversation) {
                // handle add message to current conversation
                localMessagesTree[keyThread] = this.getUpdatedCurrentConversationMessage(msg, localMessagesTree[keyThread], keyThread);
            }
            else {
                // check if message is from support mam archives
                const mainThread = this.getInstance().getMainThread();
                const { key: msgThread } = msg;
                const threadKeysArr = Object.keys(localMessagesTree).filter((thread) => {
                    return thread.includes(`${mainThread}|$|`);
                });
                // console.log("threadKeysArr: ", threadKeysArr);
                // only add to if conversation is from support
                if (threadKeysArr.includes(keyThread)) {
                    // if msg thread is the same as key thread
                    // means archive case already exists
                    if (msgThread === keyThread) {
                        const currentMamId = this.getWebchatInstance().getMamIdFromStanza(msg.stanza);
                        // check for duplicates
                        const isMessageAlreadyExists = this.getWebchatInstance().isMamMessageExistsOnConversation([...localMessagesTree[keyThread]], msg, currentMamId);
                        // console.log("isMessageAlreadyExists: ",isMessageAlreadyExists);
                        // console.log("currentMamId: ", currentMamId);
                        if (!isMessageAlreadyExists || !currentMamId) {
                            if (isCurrentConversationPaginationModeForward) {
                                console.log("receiving mam message with forward pagination from support...");
                                localMessagesTree[keyThread].push(msg);
                                // Add message into messages array store
                                this.getWebchatInstance().addMessage(msg);
                                // Add message into conversation history
                                this.getWebchatInstance().addMessageToHistory(msg);
                                // fire unread message
                                this.handleFireUnreadMessages(keyThread);
                            }
                            else {
                                console.log("receiving mam message with backward pagination from support...");
                                // again, we have to wait all the previous support messages to arrive first
                                // so lets stage the results of the pagination for the moment
                                // and only prepend to the correct thread when all messages arrives
                                this.getWebchatInstance().pushMessageInCurrentConversationSupportPagination({
                                    thread: keyThread,
                                    message: msg
                                });
                            }
                        }
                    }
                }
                else {
                    // this happens when user switch to a support conversation
                    // while spoc hasnt replied yet,
                    // then apparently, spoc replies late
                    // so we only detect spoc message
                    if (keyThread === mainThread) {
                        console.log("message is from spoc while a support conversation is open...");
                        const isMessageOkToBeAddedToConversation = this.isMessageOkToBeAddedToConversation(msg, [
                            ...localMessagesTree[keyThread]
                        ]);
                        // always check if message is ok be added
                        if (isMessageOkToBeAddedToConversation) {
                            console.log("message is ok to be added to spoc conversation...");
                            // Add message into conversation history
                            this.getWebchatInstance().addMessageToHistory(msg);
                            // handle add message to the conversation
                            localMessagesTree[keyThread].push(msg);
                            // handle disable is typing bubble
                            this.getInstance().handleShowIsTypingBubble(false);
                            // fire unread messages
                            this.handleFireUnreadMessages(keyThread);
                        }
                    }
                }
            }
            // Update localStorage
            this._slsSet(this.getInstance()._getLocalMessageTree, localMessagesTree);
            // Track last message timestamp for mam query
            const { stanza } = msg;
            let lastMsgTimestamp = stanza ? stanza.timestamp : msg.timestamp;
            if (storeInLS) {
                // store timestamp in local storage
                localStorage.setItem(this.getInstance()._getLocalLastMessageTimestamp, lastMsgTimestamp);
                // handle debounce timeout message
                // the idea is very simple, less complicated and less logic
                // here, just add a debounce timeout with a
                // configurable inactive time (i.e. 20 mins)
                this.getInstance().debounceTimeoutMessage(msg, lastMsgTimestamp);
            }
            if (!isXmppQueryArchivePagination) {
                // handle debounce auto scrolldown conversation
                // here, just add a debounce so it will trigger last
                this.getInstance().debounceScrollDownConversation(keyThread, "selective", msg);
            }
        },
        /*
         * returns modified payload for privacy message
         * i.e. { payload, adamActions }
         * removes adamActions: [] inside content
         * and reposition to the same hierarchy with content
         * otherwise return empty { adamActions: [] }
         * @param {any} payload - The message to send via XMPP
         */
        getWelcomePrivacyMessagePayload(payload = {}) {
            const { text, content } = payload;
            const privacyResponses = ["Accetto", "Rifiuto"];
            const emptyAdamActions = { adamActions: [] };
            if (privacyResponses.includes(text) && content) {
                const [privacyContent] = content;
                const { adamActions } = privacyContent;
                // sometimes adamActions is undefined
                if (adamActions) {
                    const [adamActionsContent] = adamActions;
                    delete privacyContent.adamActions;
                    return {
                        payload: {
                            ...payload,
                            content: [{ ...privacyContent }]
                        },
                        adamActions: [
                            {
                                ...adamActionsContent,
                                args: [adamActionsContent.args]
                            }
                        ]
                    };
                }
                else {
                    return { ...emptyAdamActions };
                }
            }
            else {
                return { ...emptyAdamActions };
            }
        },
        /*
         * returns modified payload for timeout message
         * i.e. {
         *  payload: {
         *    text: "Start a new conversation|Continue the previous conversation",
         *    adamActions: [{
         *      // conversation.session.continue_conversation is frontend only
         *      code: "conversation.session.restart|conversation.session.continue_conversation"
         *    }]
         *  },
         *  isTimeoutPayload: true
         * }
         * otherwise, do nothing return { isTimeoutPayload: false }
         * @param {any} payload - The message to send via XMPP
         */
        getTimeoutMessagePayload(payload = {}) {
            const { text, content } = payload;
            const timeoutResponses = [strings.defaultTimeoutContinuePreviousMessage, strings.defaultTimeoutRestartMessage];
            if (!timeoutResponses.includes(text) || !content) {
                return { isTimeoutPayload: false };
            }
            const [timeoutContent] = content;
            const { adamActions } = timeoutContent;
            if (!adamActions) {
                return { isTimeoutPayload: false };
            }
            const [adamActionsContent] = adamActions;
            const { code } = adamActionsContent;
            return {
                payload: {
                    text,
                    adamActions: [{ code }]
                },
                isTimeoutPayload: true
            };
        },
        /**
         * Send msg override
         * @override MonkChatCore.sendMessageAction method
         * @param {any} msg - The message to send
         * @returns {void}
         */
        sendMessageAction(msg) {
            // validate input for msg type chatText
            if (this.isMessageChatText(msg)) {
                const { text } = this.getMessageChatTextPayload(msg);
                /**
                 * return false if message from input
                 * is not validated and contains exploits
                 * normally some xss attacks
                 */
                if (!this.isMessageInputValidated(text)) {
                    return false;
                }
            }
            if (!this.isMessageHasStanza(msg)) {
                Object.assign(msg, { timestamp: moment().format() });
            }
            this.sendXMPPMessage(msg);
            this.storeMessage(msg);
            // show is typing bubble
            setTimeout(() => {
                this.handleFireShowTypingBubble(msg);
            }, 300);
        },
        /**
         * Send msg over XMPP
         * @override MonkChatCore.sendXMPPMessage method
         * @param {any} msg - The message to send via XMPP
         * @returns {void}
         */
        sendXMPPMessage(msg) {
            const timestamp = moment().format();
            const { currentChatThread, _parsedURIParamsForMessagePayloadData } = this.getInstance();
            const thread = currentChatThread || this.getCurrentConversationThread();
            // Creates XMPP message structure
            const message = {
                to: this.getInstance()._getReceiver,
                body: " ",
                timestamp,
                thread
            };
            // here intercept payload to check for
            // accept/reject welcome privacy message
            const { adamActions, payload } = this.getWelcomePrivacyMessagePayload({ ...msg.payload });
            // only modify payload if adamActions is available
            if (adamActions && adamActions.length && payload) {
                const messageResponse = { ...msg };
                msg = {
                    ...messageResponse,
                    payload: {
                        ...payload,
                        adamActions
                    }
                };
                // enable privacyMessageClick
                this.getInstance().toggleIsPrivacyMessageClick(true);
                // disable isUserRelogin flag
                this.getInstance().isUserRelogin = false;
            }
            // intercept checkpoint for timeout message payload(i.e. continue/restart)
            const { isTimeoutPayload, payload: payloadTimeout } = this.getTimeoutMessagePayload({ ...msg.payload });
            // only modify payload if isTimeoutPayload is true
            if (isTimeoutPayload && payloadTimeout) {
                const messageResponse = { ...msg };
                msg = {
                    ...messageResponse,
                    payload: {
                        ...payloadTimeout
                    }
                };
                // if payload is continue conversation, dont send
                if (this.isMessageContinueConversation(msg)) {
                    return false;
                }
            }
            // Add additional data getting it from URL
            Object.assign(msg.payload, {
                data: _parsedURIParamsForMessagePayloadData
            });
            // Send XMPP message via Huangcha
            try {
                console.log("sendXMPPMessage payload msg: ", JSON.stringify(msg));
                this.getWebchatInstance().instance.sendMonkMessage(message, msg, { askReceipt: true });
                // Handle adamActions
                const { content } = msg.payload;
                if (content) {
                    content.forEach((item) => this.handleAdamActions(item));
                }
            }
            catch (err) {
                console.log("sendXMPPMessage error: ", err);
            }
        },
        /**
         * Fetch messages from localStorage or from the server and trigger all the observers
         * @override MonkChatCore.retrieveOlderMessages method
         * @returns {void}
         */
        retrieveOlderMessages() {
            const localMessagesTree = this.getInstance().getLocalMessagesDictionary();
            const { class: currentChatClass } = this.getWebchatInstance().currentChat;
            const isWfp = currentChatClass === ConversationType.Wfp;
            // get mam from 3 days ago
            // last updated 1/30/25
            // const correctedTimestamp = this.getInstance().getCorrectedTimestamp(3);
            const correctedTimestamp = this.getInstance().getCorrectedTimestamp(3);
            const receiver = this.getInstance()._getReceiver;
            if (localMessagesTree && Object.values(localMessagesTree).length > 0) {
                Object.values(localMessagesTree).forEach((conversation) => {
                    conversation.forEach((msg) => {
                        const { thread } = msg;
                        // Check if message has thread field (inside payload > data)
                        if (thread) {
                            msg.thread = thread;
                        }
                        // TODO: understand why we even need this?
                        this.getWebchatInstance().addMessage(msg);
                    });
                });
                // Check for eventual messages from server, starting from most recent message.
                // This is useful to fetch messages between now and the last time that the user used the chat.
                // TODO: revisit if this comes first or the handleWelcomeMessage below
                if (isWfp) {
                    localStorage.setItem(this.getInstance()._getLocalLastMessageTimestamp, correctedTimestamp);
                    console.log("has localMessagesTree corrected timestamp for spoc: ", correctedTimestamp);
                }
                const mainThread = this.getInstance().getMainThread();
                if (localMessagesTree.hasOwnProperty(mainThread)) {
                    // we need to first, toggle force query archive pagination
                    this.getWebchatInstance().setShouldForceQueryArchivePagination(true);
                    // then, get lastMamId
                    const lastMamId = this.getWebchatInstance().getXmppMamPaginationLastId(localMessagesTree[mainThread], true);
                    console.log("lastMamId before fetching next messages: ", lastMamId);
                    // call mam after pagination request
                    this.getWebchatInstance().handleFetchNextMessages(lastMamId);
                }
            }
            else {
                // ...Otherwise fetches them from the server
                // only correct the timestamp for spoc conversations
                if (isWfp) {
                    // set corrected timestamp to localStorage
                    localStorage.setItem(this.getInstance()._getLocalLastMessageTimestamp, correctedTimestamp);
                    console.log("no localMessagesTree corrected timestamp for spoc: ", correctedTimestamp);
                }
                this.getWebchatInstance().sendRawMamIqRequest({
                    receiver,
                    beforeMsgId: "",
                    afterMsgId: false,
                    max: this.lastMessageToDisplayTotal,
                    isQueryArchive: true
                });
                // this.getInstance().handleWelcomeMessage(true);
            }
            // TODO: remove after dev
            /* setTimeout(() => {
              // console.log('render mock restart....');
              // this.getInstance().handleRestartMessage();
      
              console.log('render mock timeout message....');
              this.getInstance().handleTimeoutMessage(true);
            }, 1000); */
            // Check for special URL params to perform quick actions (usually send messages without XMPP interaction)
            this.getInstance().checkChatQuickActionsFromURI();
        },
        /**
         * Handle UX based on incoming message
         * @override MonkChatCore.fillOutMessagesList method
         * @param {any} msg - The message that triggered this observer
         * @returns {void}
         */
        async fillOutMessagesList(msg) {
            const localMessagesTree = this.getInstance().getLocalMessagesDictionary();
            let messageSender = "bot";
            let incomingThread = "";
            if (msg) {
                const { payload, stanza } = msg;
                // handle sender
                messageSender =
                    payload && payload.hasOwnProperty("data") && payload.data.hasOwnProperty("conversationType")
                        ? msg.payload.data.conversationType
                        : "bot";
                // handle thread
                if (stanza) {
                    const { thread } = stanza;
                    incomingThread = thread;
                }
            }
            const currentConversationPaginationMode = this.getWebchatInstance().getCurrentConversationPaginationMode;
            if (isMamMessage(msg) && currentConversationPaginationMode === "forward") {
                const lastMamId = this.parseMamArchiveIdFromXML(msg.stanza.rawStanza);
                // console.log("fillOutMessagesList lastMamId: ", lastMamId);
                localStorage.setItem(this.getInstance()._getLocalLastMamMessageId, lastMamId);
            }
            // Perform logic on messages only if there's a valid thread
            if (incomingThread) {
                if (isMamMessage(msg)) {
                    Object.assign(msg, {
                        timestamp: this.getInstance().extractTimestampFromMamMessage(msg)
                    });
                }
                if (!localMessagesTree.hasOwnProperty(incomingThread)) {
                    localMessagesTree[incomingThread] = [];
                }
                // Redundant logic...
                // Reset thread if it's guest message
                incomingThread = incomingThread === "anonymous" ? "" : incomingThread;
                const splitConversations = this.getWebchatInstance().checkIfChatIsMultiLevel(incomingThread);
                // Check if it's message from human...
                if (splitConversations.length > 0) {
                    // handle conversation message from human
                    await this.handleConversationMessageFromHuman(incomingThread, messageSender, localMessagesTree, msg);
                }
                else {
                    // if message from SPOC
                    // disable isTyping bubble
                    // this.getInstance().handleShowIsTypingBubble(false);
                    console.log("fillout messages spoc...");
                    this.storeMessage(msg);
                    // handle debounce archive conversation
                    // the idea is very simple, less complicated and less logic
                    // here, just add a debounce so it will trigger last
                    // but only if mam query is not pagination
                    const { isXmppQueryArchivePagination } = this.getWebchatInstance();
                    if (!isXmppQueryArchivePagination) {
                        this.getInstance().debounceArchiveConversationMessage();
                    }
                }
            }
        },
        async handleConversationMessageFromHuman(incomingThread, messageSender, localMessagesTree, msg) {
            const localConversationsTree = this.getInstance().getLocalConversationsArray();
            const splitConversations = this.getWebchatInstance().checkIfChatIsMultiLevel(incomingThread);
            let canUpdateConversationsTree = true;
            // Extract conversation title
            const conversationTitle = msg.hasOwnProperty("payload") &&
                msg.payload.hasOwnProperty("data") &&
                msg.payload.data.hasOwnProperty("chatTitle")
                ? msg.payload.data.chatTitle
                : atob(splitConversations[1].split("_")[0]);
            // Create new conversation if hasn't been already created...
            const isLocalConversationExisted = localConversationsTree.find((item) => item.thread === incomingThread);
            // console.log("isLocalConversationExisted: ", isLocalConversationExisted);
            if (!isLocalConversationExisted) {
                // ... if not, create it
                const newConversationToAdd = {
                    id: `${incomingThread}__${Date.now()}`,
                    title: conversationTitle,
                    class: ConversationType.Active,
                    avatar: this.getInstance()._getRenderedAsset.ico_operatore,
                    thread: incomingThread,
                    is_archived: false,
                    timestamp: isMamMessage(msg)
                        ? !this.isMessageHasStanza(msg)
                            ? msg.timestamp
                            : msg.stanza.timestamp
                        : moment().format()
                };
                localConversationsTree.push(newConversationToAdd);
                await this.getWebchatInstance().pushConversation(newConversationToAdd);
                // Redundant logic...
                // Handle FOREGROUND chat notice (if it's a notice)
                if (!isMamMessage(msg)) {
                    this.getWebchatInstance().handleNotice(msg);
                }
            }
            else {
                // ... Otherwise, if conversation is already created
                // Check if message is a chat_notice
                if (msg.payload.hasOwnProperty("code")) {
                    const currentChatIndex = localConversationsTree.findIndex((lc) => lc.thread === incomingThread);
                    // Handle notices
                    switch (msg.payload.code) {
                        case noticeCodes[0]: // GENESYS#0001
                        case noticeCodes[1]: // GENESYS#0002
                        case noticeCodes[3]: // GENESYS#0004
                            if (currentChatIndex !== -1) {
                                // Genesys close message, but not when chat's archived
                                if (msg.payload.code === noticeCodes[3]) {
                                    if (localConversationsTree[currentChatIndex].class !== ConversationType.Archived) {
                                        localConversationsTree[currentChatIndex].class = ConversationType.Deleted;
                                    }
                                    else if (localConversationsTree[currentChatIndex].class === ConversationType.Archived) {
                                        localConversationsTree[currentChatIndex].is_archived = true;
                                    }
                                }
                                else if (localConversationsTree[currentChatIndex].class !== ConversationType.LocalClosed) {
                                    localConversationsTree[currentChatIndex].class = ConversationType.RemoteClosed;
                                }
                            }
                            // handle UI checking context
                            if (this.isMessageHasStanzaWithEqualThread(msg)) {
                                this.getInstance().showCloseChatCta = false;
                                this.getWebchatInstance().toggleUserInputArea(false);
                            }
                            break;
                        case noticeCodes[4]: // GENESYS_CLOSE_BY_USER#0001
                            if (currentChatIndex !== -1) {
                                localConversationsTree[currentChatIndex].class = ConversationType.LocalClosed;
                                console.log("noticeCodes[4]: ", noticeCodes[4]);
                                this._slsSet(this.getInstance()._getLocalMessageTree, localMessagesTree);
                                // handle UI checking context
                                if (this.isMessageHasStanzaWithEqualThread(msg)) {
                                    this.getInstance().showCloseChatCta = false;
                                    this.getWebchatInstance().toggleUserInputArea(false);
                                }
                            }
                            break;
                        case noticeCodes[6]: // FOREGROUND_CONVERSATION#0001
                        case noticeCodes[7]: // FOREGROUND_CONVERSATION#0002
                            if (currentChatIndex !== -1) {
                                if (localConversationsTree[currentChatIndex]) {
                                    localConversationsTree[currentChatIndex].class = ConversationType.Active;
                                }
                            }
                            if (!isMamMessage(msg)) {
                                this.getWebchatInstance().handleNotice(msg);
                            }
                            break;
                    }
                    // Update conversation class
                    if (currentChatIndex !== -1) {
                        await this.getWebchatInstance().updateConversationClass({
                            thread: localConversationsTree[currentChatIndex].thread,
                            newClass: localConversationsTree[currentChatIndex].class
                        });
                    }
                }
                else if (msg.payload.hasOwnProperty("adamActions")) {
                    // Check if message is an adamAction
                    // Reset close CTA (hide it)
                    this.getInstance().showCloseChatCta = false;
                    // Handle close CTA
                    this.handleAdamActions(msg.payload);
                }
                else {
                    // Handle UI based on incoming message, like:
                    if (isIncomingMessage(msg)) {
                        const isCurrentChat = Object.entries(this.getWebchatInstance().currentChat).length >= 0;
                        const chatCanBeInteractedByUser = isCurrentChat &&
                            !this.getInstance().canShowConversationsView &&
                            incomingThread === this.getCurrentConversationThread() &&
                            messageSender !== "bot";
                        // Toggle close CTA
                        this.getInstance().showCloseChatCta = chatCanBeInteractedByUser;
                        // Toggle attachments
                        this.prepareAttachmentsInformations(chatCanBeInteractedByUser);
                        canUpdateConversationsTree = false;
                    }
                }
            }
            // Check if message can be added onto UI (avoid duplicates)
            if (this.isMessageHasToBeAdded(msg, incomingThread)) {
                if (isMamMessage(msg)) {
                    Object.assign(msg, {
                        timestamp: this.getInstance().extractTimestampFromMamMessage(msg)
                    });
                }
                this.storeMessage(msg);
            }
            if (canUpdateConversationsTree) {
                this._slsSet(this.getInstance()._getLocalConversationTree, localConversationsTree);
            }
        },
        /**
         * A WORKAROUND method, useful to update conversations UI array. Require refactor!
         * Update chat UI (title & icon) based on incoming message
         */
        sanitizeConversationsArray() {
            const conversationListRef = this.getWebchatInstance().$refs.conversationList;
            let sanitizedArray = [];
            if (!conversationListRef.isArchiveSection) {
                const localConversationsTree = this.getInstance().getLocalConversationsArray();
                sanitizedArray = localConversationsTree.reverse();
            }
            else {
                const archivedConversationsTree = this.getInstance().getLocalConversationsArray(true);
                sanitizedArray = archivedConversationsTree;
            }
            this.getWebchatInstance().setConversationsArray(sanitizedArray);
        },
        parseMamArchiveIdFromXML(xmlMessage) {
            const parser = new DOMParser();
            const xmlMsg = parser.parseFromString(xmlMessage, "text/xml");
            // @ts-ignore
            return xmlMsg.firstElementChild && xmlMsg.firstElementChild.firstElementChild
                ? xmlMsg.firstElementChild.firstElementChild.id
                : "";
        },
        /**
         * Checks if message can be addded into current conversation
         * @param {any} message - Incoming message
         * @param {string} keyThread - Conversation thread
         * @returns {void}
         */
        isMessageHasToBeAdded(message, keyThread) {
            const localMessagesTree = this.getInstance().getLocalMessagesDictionary() || {};
            const { stanza } = message;
            if (!stanza) {
                return false;
            }
            const { rawStanza } = stanza;
            if (!rawStanza) {
                return false;
            }
            if (!localMessagesTree.hasOwnProperty(keyThread)) {
                return true;
            }
            const currentMamId = this.getWebchatInstance().getMamIdFromStanza(message.stanza);
            let isNotMamMessage = false;
            // check if message exist
            const isMessageAlreadyExists = localMessagesTree[keyThread].find((item) => {
                const { stanza } = item;
                // console.log("item: ", JSON.stringify(item));
                if (stanza) {
                    const itemMamId = this.getWebchatInstance().getMamIdFromStanza(stanza);
                    // console.log("itemMamId: ", itemMamId);
                    return itemMamId === currentMamId;
                }
                else {
                    // if dont have stanza, not a mam message
                    // console.log("itemMamId: none");
                    isNotMamMessage = true;
                    return false;
                }
            });
            // if message do not exist or
            // currentMamId do not exist(this happens for forwarded messages)
            return !isMessageAlreadyExists || !currentMamId;
        },
        handleRenderPrivacyMessageInputArea(currentConversation = []) {
            const { isUserRelogin, isPrivacyMessageClick, showIsTypingBubble } = this.getInstance();
            const hasMessages = currentConversation.length > 1;
            const showWelcomeMessage = 
            // 1st. case
            (isUserRelogin && hasMessages) ||
                // 3rd. case
                (!isUserRelogin && hasMessages && !showIsTypingBubble && !isPrivacyMessageClick) ||
                // 4th. case
                (!isUserRelogin && (!hasMessages || showIsTypingBubble));
            const forceIsWfp = 
            // 1st. case
            isUserRelogin ||
                // 2nd. case
                (!isUserRelogin && hasMessages && !showIsTypingBubble && isPrivacyMessageClick) ||
                // 3rd. case
                (!isUserRelogin && hasMessages && !showIsTypingBubble && !isPrivacyMessageClick);
            // 2nd. case
            const forceIsPrivacy = !isUserRelogin && hasMessages && !showIsTypingBubble && isPrivacyMessageClick;
            if (!!showWelcomeMessage) {
                console.log("handleRenderPrivacyMessageInputArea !!showWelcomeMessage handleWelcomeMessage...");
                this.getInstance().handleWelcomeMessage(true);
            }
        },
        /**
         * Shows selected conversation and toggles all the related UI components
         * @override MonkChatCore.renderCurrentConversation method
         * @param {any} conversation - Conversation to render
         * @returns {void}
         */
        renderCurrentConversation(conversation) {
            const { thread, class: conversationClass } = conversation;
            const keyThread = thread ? thread : this.getCurrentConversationThread();
            const canShowConversationsView = Object.entries(conversation).length === 0;
            const localMessagesTree = this._slsGet(this.getInstance()._getLocalMessageTree);
            this.getWebchatInstance().canShowConversationsView = canShowConversationsView;
            // is chat view
            if (!canShowConversationsView) {
                // disable force query pagination
                this.getWebchatInstance().setShouldForceQueryArchivePagination(false);
                if (conversationClass !== ConversationType.Wfp) {
                    this.getWebchatInstance().filterConversationsMessages(keyThread, "", "", localMessagesTree);
                    this.getInstance().currentChatThread = keyThread;
                    this.getInstance().setupConversationUI(conversation);
                    if (conversationClass === ConversationType.Active) {
                        console.log("support...");
                        this.prepareAttachmentsInformations();
                        this.getWebchatInstance().toggleUserInputArea(true);
                        this.getWebchatInstance().toggleUserInputStatus(true);
                        this.getInstance().handleWelcomeMessage();
                        this.getInstance().showCloseChatCta = !this.getInstance().canShowConversationsView;
                        // disable restart chat
                        this.getWebchatInstance().setShowRestartChatButton(false);
                        // reset
                        this.getWebchatInstance().setModeBeforeSubmit("");
                        this.getWebchatInstance().setVisibilityAfterSubmit("");
                        this.getInstance().handleShowIsTypingBubble(false);
                        // reset unread messagges
                        this.handleFireResetUnreadMessages(keyThread);
                    }
                    else {
                        console.log("...");
                        this.getWebchatInstance().toggleUserInputArea(false);
                        this.getInstance().showCloseChatCta = false;
                        // disable restart chat
                        this.getWebchatInstance().setShowRestartChatButton(false);
                        // reset unread messagges
                        this.handleFireResetUnreadMessages(keyThread, "selective");
                    }
                }
                else {
                    console.log("spoc...");
                    this.getWebchatInstance().filterConversationsMessages(keyThread, conversationClass, "", localMessagesTree);
                    this.getInstance().currentChatThread = keyThread;
                    this.getWebchatInstance().toggleUserInputStatus(true);
                    this.getInstance().showCloseChatCta = false;
                    this.prepareAttachmentsInformations(false);
                    this.getInstance().setupConversationUI(conversation);
                    // this.getInstance().handleWfpConversationReset();
                    this.getWebchatInstance().toggleUserInputArea(true);
                    // enable restart chat
                    this.getWebchatInstance().setShowRestartChatButton(true);
                    // TODO: confirm if input area is always enable
                    // theres been a report about input area being disable
                    // when comes from list view to conversation view
                    // handle input modes before/after
                    this.getInstance().handleInputModesBeforeAfter();
                    // reset unread messagges
                    this.handleFireResetUnreadMessages(keyThread);
                    // handle privacy message and input chat area
                    // this.handleRenderPrivacyMessageInputArea(getCurrentChatMessages);
                }
            }
            else {
                this.getInstance().showCloseChatCta = false;
            }
        },
        /**
         * Check for "adamActions" and perform logic on commands (if found)
         * @param {any} content - Message content
         * @returns {void}
         */
        handleAdamActions(content = {}) {
            const { adamActions } = content;
            if (!adamActions) {
                return false;
            }
            adamActions.forEach((action) => {
                let uiStatus = true;
                switch (action.code) {
                    case adamActionsConfig[0]:
                        // Disable user input area
                        uiStatus = false;
                        break;
                    default:
                        // Enable user input area
                        uiStatus = true;
                        break;
                }
                this.getWebchatInstance().toggleUserInputStatus(uiStatus);
            });
        }
    }
});
let App = class App extends Vue {
    constructor() {
        super(...arguments);
        // XMPP domain
        this.DOMAIN = "wfp";
        // XMPP receiver
        this.RECEIVER = "va";
        // Back-end API version (needed from BE)
        this.API_VERSION = 5;
        this.ATTACHMENT_ENDPOINT = "";
        // LS_ means localStorage aliases
        this.LS_USER_COOKIE_CREDS_KEY = "_wcu";
        this.LS_XMPP_RESOURCE = "resource";
        this.LS_AUTO_CLEAN_LS_COUNTER = "_clean_ls_ctr";
        this.LS_GOOGLE_MAPS_API_LANG = "_gmaps_lang";
        this.LS_CURRENT_USER_THREAD = "_t";
        this.LS_MESSAGE_TREE = "_lmt_";
        this.LS_CONVERSATION_TREE = "_lct_";
        this.LS_CONVERSATION_COUNTER = "_lcc_";
        this.LS_LAST_MAM_MESSAGE_ID = "_last_mam_id_";
        this.LS_LAST_MESSAGE_TIMESTAMP = "_last_msg_ts_";
        this.fetchWfpConversationDaysLimit = 7; // refers to no. of days
        this.currentChatThread = "";
        this.showLoader = true;
        this.userCredentials = { EIMeVersion: 2 };
        this.showCloseChatCta = false;
        this.additionalURLParams = { useChatDynamic: true };
        this.chatHeaderTitle = "";
        this.showDialog = false;
        this.showChatBackButton = true;
        this.isChatConversationListVisited = false;
        this.firstChatUpdateDone = false;
        // wfp confirm user actually click the privacy message, default:false
        this.isPrivacyMessageClick = false;
        this.sessionIntervalCycle = 0;
        // for dev testing
        // reduce to smaller number to test force xmpp logout(e.g. 20)
        this.sessionIntervalCyclesLimit = 1800; // 30 min is 1800 sec
        this.chatMessageCount = 0; // is the total count of the exchange messages
        this.isTypingBubbleTimeoutSecs = 0; // timeout in secs
        this.showIsTypingBubbleSecs = 0; // no of sec before showing the typing bubble
        this.messageTimeoutSecs = 0; // timeout in secs
        this.shouldAllowLoginQueryParams = false; // flag to allow webchat to login with creds from query string
        this.xmppMessagePriority = 0; // xmpp message priority
        this.lastMessageToDisplayTotal = 50; // the last message to display count, spoc conversation only
        this.shouldAutoCleanLS = false; // flag to autoclean local storage
        this.autoCleanAttemptCount = 1; // the number of attempts to auto clean ls, default is 1
        this.googleMapsApiLang = "en"; // the indicated language use for google maps api, default is "en"
        this.showIsTypingBubble = false; // flag to show is typing
        this.showVerificationPopup = true; // flag to always show the verification popup before using the chat
        this.showVerificationPopupDeclined = false; // flag that confirms verification is declined
        this.isUserRelogin = false; // flag to identify new session
        this.debugMode = false;
    }
    get isNotVerificationDeclined() {
        return !this.showVerificationPopupDeclined;
    }
    /**
     * @returns {VueInstance} The instance of Webchat component reference.
     */
    get _webchatReference() {
        return this.$refs.webchat;
    }
    /**
     * @returns {VueInstance} The instance of Webchat component reference.
     */
    get _webchatConversationListReference() {
        return this._webchatReference.$refs.conversationList;
    }
    /**
     * @returns {string} Current decrypted "resource" from localStorage
     */
    get _resource() {
        return this._slsGet(this.LS_XMPP_RESOURCE);
    }
    get _getAutoCleanAttemptCounter() {
        return parseInt(this._slsGet(this.LS_AUTO_CLEAN_LS_COUNTER)) || 1;
    }
    get _getLastMamId() {
        return localStorage.getItem(this._getLocalLastMamMessageId) || "";
    }
    get _getLocalMessageTree() {
        return `${this.LS_MESSAGE_TREE}${this._resource}`;
    }
    get _getLocalConversationTree() {
        return `${this.LS_CONVERSATION_TREE}${this._resource}`;
    }
    get _getLocalConversationsCounter() {
        return `${this.LS_CONVERSATION_COUNTER}${this._resource}`;
    }
    get _getLocalLastMamMessageId() {
        return `${this.LS_LAST_MAM_MESSAGE_ID}${this._resource}`;
    }
    get _getLocalLastMessageTimestamp() {
        return `${this.LS_LAST_MESSAGE_TIMESTAMP}${this._resource}`;
    }
    get _getReceiver() {
        return `${this.RECEIVER}@${this.DOMAIN}`;
    }
    get _randomId() {
        return Math.random().toString(36).substr(2, 9);
    }
    get _getRenderedAsset() {
        return {
            avatar_wfp: this.renderAsset("wfp-icons/bot.svg"),
            avatar_operatore: this.renderAsset("wfp-icons/headphones.svg"),
            ico_user: this.renderAsset("wfp-icons/avatar.svg"),
            ico_wfp: this.renderAsset("wfp-icons/bot.svg"),
            ico_operatore: this.renderAsset("wfp-icons/headphones.svg")
        };
    }
    /**
     * @returns {Conversation} Default SPOC conversation object
     */
    get _wfpChat() {
        return {
            title: "SPOC",
            class: ConversationType.Wfp,
            avatar: this._getRenderedAsset.avatar_wfp,
            pin_date: Date.now() * 1000 // to be sure that Wfp's always on top of list
        };
    }
    /**
     * @returns {Array} Allowed set of parameters fetchable from URL
     */
    get _allowedUriParams() {
        return ["user", "token", "thread"];
    }
    /**
     * @returns {Object} Default "data" field to be passed into message payload
     */
    get _parsedURIParamsForMessagePayloadData() {
        return {
            ..._.omit(this.additionalURLParams, ["action", "thread"]),
            os: "web",
            apiVersion: this.API_VERSION,
            appVersion
        };
    }
    get _getDefaultChatConversationHeaderTitle() {
        return `<strong class="slogan">${this.chatHeaderTitle}</strong>`;
    }
    get _getChatConversationHeaderTitle() {
        const defaultTitle = [];
        defaultTitle.push(this._getDefaultChatConversationHeaderTitle);
        defaultTitle.push(`<span class="slogan-desc">${strings.defaultWfpHeaderPrivacy}</span>`);
        return defaultTitle.join("<br/>");
    }
    get _getVerificationPopupText() {
        return {
            content: strings.defaultVerificationPopupText,
            contentDecline: strings.defaultVerificationDeclinePopupText,
            confirmButton: strings.verificationPopupConfirmButtonText,
            declineButton: strings.verificationPopupDeclineButtonText,
        };
    }
    get _getTimeoutMessageMillisecond() {
        // 1 sec = 1000 millisecond
        return this.messageTimeoutSecs * 1000;
    }
    get _getShowIsTypingBubbleMillisecond() {
        // 1 sec = 1000 millisecond
        return this.showIsTypingBubbleSecs * 1000;
    }
    get getLabel() {
        return {
            heading_conversation_list: strings.defaultConversationListHeading,
            heading_cases_history: strings.defaultCasesHistoryHeading,
            label_back_to_conversation_list: strings.defaultBackToConversationListLabel,
            label_ask_bot: strings.defaultAskBotLabel,
            label_xmpp_connection_message: strings.defaultXmppConnectionMessage,
            label_select_button_first: strings.defaultSelectButtonLabel,
            label_input_text_placeholder: strings.defaultInputTextPlaceholder,
            label_input_date_placeholder: strings.defaultInputDatePlaceholder,
            label_input_location_placeholder: strings.defaultInputLocationPlaceholder,
            label_popup_location_placeholder: strings.defaultPopupLocationPlaceholder,
            label_popup_button_cancel: strings.defaultPopupButtonCancel,
            label_popup_button_confirm: strings.defaultPopupButtonConfirm
        };
    }
    get getAssets() {
        return {
            // new_question|restart
            icon_restart: "new_question"
        };
    }
    get getBotConfigSettings() {
        return {
            labels: { ...this.getLabel },
            assets: { ...this.getAssets },
            googleMapsApiLangKey: this.LS_GOOGLE_MAPS_API_LANG
        };
    }
    _slsGet(key, type = "") {
        return this._webchatReference.slsGet(key, type);
    }
    _slsSet(key, value) {
        this._webchatReference.slsSet(key, value);
    }
    created() {
        // setup xmpp env overrides
        this.setupXmppEnvConfig();
        this.$on("CLOSE_CHAT_DIALOG", (status) => (this.showDialog = status));
        this.parseURLParams();
        // with vue2, its important to put debounce inside created() hook and not on methods
        // because the component is using export default { ... },
        // including the methods are going to be reused by all the instances of the component
        // which means there could be duplicates of the same debounce that we want to prevent
        this.debounceTimeoutMessage = debounce((msg, lastMsgTimestamp) => {
            this.handleLastConversationTimeoutMessage(msg, lastMsgTimestamp);
        }, this._getTimeoutMessageMillisecond);
        // for archives
        this.debounceArchiveConversationMessage = debounce(() => {
            // handle welcome message
            this.handleWelcomeMessage();
            // handle input modes before/after modes
            this.handleInputModesBeforeAfter();
        }, 1000);
        // scrolldown conversation
        this.debounceScrollDownConversation = debounce((keyThread, mode, msg) => {
            // reset unread messages
            this._webchatReference.handleFireResetUnreadMessages(keyThread, mode, msg);
            // handle scrolldown
            this._webchatReference.onScrollDownConversation();
            console.log("auto scrolling down...");
        }, 500);
        // handle show is typing bubble
        /* this.debounceShowTypingBubble = debounce((toggle) => {
          // toggle show typing bubble
          this.handleShowIsTypingBubble(toggle);
        }, this._getShowIsTypingBubbleMillisecond); */
    }
    beforeDestroy() {
        this.$off("CLOSE_CHAT_DIALOG");
    }
    mounted() {
        console.log(`v${appVersion}`);
        // handle auto clean ls
        this.handleAutoCleanLocalStorages();
        // handle fetching login creds
        this.handleFetchXmppCredsFromExternal((params) => {
            // handle before login processes
            this.handleChecksBeforeLogin(params)
                .then(() => {
                // login
                this.loginAsUser();
                // clear all session first
                this.handleClearSessionInspector();
                // activate the session inspector
                this.setupConnectionSessionInspector();
                // setup conversation tree
                this.setupConversationTree();
                // handle chat display
                this.renderConversationsOrShowDedicatedChat();
                // @ts-ignore
                this.showLoader = false;
                this.initAttachmentsUploader();
            })
                .catch((err) => {
                // @ts-ignore
                this.showLoader = false;
                console.error(err);
            });
        });
    }
    getMainThread() {
        return mainThread(this.additionalURLParams.thread);
    }
    getLocalUserCreds() {
        const userCreds = this._slsGet(this.LS_USER_COOKIE_CREDS_KEY);
        // if userCreds is available from localStorage
        // returns an encoded string
        // otherwise returns empty object
        return typeof userCreds === "string" && userCreds !== "" ? JSON.parse(atob(userCreds)) : { user: "", password: "" };
    }
    isLocalUserCredsExists() {
        const { user, password } = this.getLocalUserCreds();
        return user !== "" && password !== "";
    }
    isLocalUserCredsTheSame(xmppCreds) {
        if (!this.isLocalUserCredsExists()) {
            return false;
        }
        const { user } = xmppCreds;
        const { user: userLocal } = this.getLocalUserCreds();
        return userLocal === user;
    }
    handleAutoCleanLocalStorages() {
        let autoCleanAttemptCounter = this._getAutoCleanAttemptCounter;
        if (this.shouldAutoCleanLS && autoCleanAttemptCounter <= this.autoCleanAttemptCount) {
            console.log("attempting to auto clean local storage, attempt count is ", autoCleanAttemptCounter);
            // reset all existing local storage
            localStorage.clear();
        }
    }
    handleUpdateAutoCleanAttemptCounter() {
        let autoCleanAttemptCounter = this._getAutoCleanAttemptCounter;
        // increment until autoclean count
        if (this._getAutoCleanAttemptCounter <= this.autoCleanAttemptCount) {
            autoCleanAttemptCounter++;
        }
        // ensure the clean attempt is updated
        this._slsSet(this.LS_AUTO_CLEAN_LS_COUNTER, autoCleanAttemptCounter);
    }
    handleChecksBeforeLogin(params) {
        return new Promise((resolve, reject) => {
            const { user, token, thread } = params;
            if (user && token && thread) {
                let userCreds = { user, password: token };
                this.setXmppCreds({ ...params, user: encodeURIComponent(user) });
                // TODO: leave this as is for now,
                // additionalURLParams is important
                // as its so tied with business logic
                // we cant just simple remove them
                // without proper testing afterwards
                this.additionalURLParams = {
                    ...this.additionalURLParams,
                    token,
                    thread
                };
                setTimeout(() => {
                    // if user is not the same
                    if (!this.isLocalUserCredsTheSame(params)) {
                        // reset all existing local storage
                        // solves everything, without selective reset
                        localStorage.clear();
                    }
                    // then, update user and thread
                    // update the token with the same user
                    this._slsSet(this.LS_USER_COOKIE_CREDS_KEY, btoa(JSON.stringify(userCreds)));
                    // update thread
                    this._slsSet(this.LS_CURRENT_USER_THREAD, thread);
                    // update google maps api language
                    this._slsSet(this.LS_GOOGLE_MAPS_API_LANG, this.googleMapsApiLang);
                    // update auto clean attempt counter
                    this.handleUpdateAutoCleanAttemptCounter();
                    resolve("Before login operations successful.");
                }, 400);
            }
            else {
                reject("Missing parameters before login operation.");
            }
        });
    }
    handleAdjustSpocConversationMessage() {
        // we need to adjust conversation messages from spoc
        // to only the last 10 messages(atleast for now)
        // but its configurable at build time from here lastMessageToDisplayTotal
        const localMessagesTree = this.getLocalMessagesDictionary();
        const mainThread = this.getMainThread();
        // if localStorage has spoc conversation,
        // we need to adjust the number of conversation messages
        // to last message to display count
        if (localMessagesTree.hasOwnProperty(mainThread)) {
            if (localMessagesTree[mainThread].length > this.lastMessageToDisplayTotal) {
                const lastConversationMessages = localMessagesTree[mainThread].slice(-this.lastMessageToDisplayTotal);
                localMessagesTree[mainThread] = [...lastConversationMessages];
                // realign localMessageTree
                this._slsSet(this._getLocalMessageTree, localMessagesTree);
            }
        }
    }
    getPreviousDateByDays(daysLimit = 0, format = "iso") {
        const today = new Date();
        today.setDate(today.getDate() - daysLimit);
        if (format === "iso") {
            return new Date(today).toISOString();
        }
        else {
            return new Date(today).toString();
        }
    }
    getCorrectedTimestamp(noOfDays = 0) {
        const daysLimit = noOfDays || this.fetchWfpConversationDaysLimit;
        const lastMsgTimestamp = localStorage.getItem(this._getLocalLastMessageTimestamp);
        const lastWeekTimestamp = this.getPreviousDateByDays(daysLimit);
        // lets default timestamp from a week old
        // as requested change 2/01/2024
        // for wfp conversations
        let correctedTimestamp = lastWeekTimestamp;
        if (lastMsgTimestamp !== null) {
            const lastMsgTimestampMilli = new Date(lastMsgTimestamp).getTime();
            const lastWeekTimestampMilli = new Date(lastWeekTimestamp).getTime();
            // if lastMsgTimestamp is greater than a week ago, ok
            if (lastMsgTimestampMilli > lastWeekTimestampMilli) {
                // update localStorage with timestamp from last message
                correctedTimestamp = new Date(lastMsgTimestamp).toISOString();
            }
        }
        return correctedTimestamp;
    }
    getSpocWelcomeMessage() {
        return this.getSetupXMPPMessageEnvelope({
            uuid: _generateUUID(),
            thread: this.getMainThread(),
            // html_message: msgContent,
            raw_payload: { ...welcomeMessagePayload }
        });
    }
    getSetupXMPPMessageEnvelope(args = {}) {
        const { html_message, raw_payload, subject, type, action, uuid, direction, thread } = args;
        const simplePayload = {
            content: [
                {
                    type: "chat_html",
                    html: html_message,
                    is_notice: false
                }
            ]
        };
        let envelope = {
            subject: subject || "dynamic",
            type: type || "chat_dynamic",
            action: action || "chat_dynamic",
            stanza: {
                id: uuid,
                direction: direction || "in",
                thread,
                timestamp: moment().format()
            },
            payload: {},
            avatar: this._getRenderedAsset.avatar_wfp
        };
        if (raw_payload) {
            envelope = {
                ...envelope,
                payload: raw_payload
            };
        }
        else {
            envelope = {
                ...envelope,
                payload: simplePayload
            };
        }
        return envelope;
    }
    checkChatQuickActionsFromURI() {
        const URIActions = this.additionalURLParams?.action?.split(",");
        if (URIActions && URIActions.length) {
            URIActions.forEach((str) => {
                // @ts-ignore
                const match = this.$store.state.quickActionsMap.find((action) => action.key === str);
                if (match) {
                    const conversationWhereToAct = this.getLocalConversationsArray().find((conversation) => conversation.class === match.conversation);
                    const silentMessage = this.getSetupXMPPMessageEnvelope({
                        uuid: _generateUUID(),
                        direction: "out",
                        thread: conversationWhereToAct.thread || this.getMainThread(),
                        html_message: match.message
                    });
                    this._webchatReference.sendMessageAction(silentMessage);
                }
            });
        }
    }
    setupXmppEnvConfig() {
        // override XMPP endpoint if present in config file
        if (process.env.VUE_APP_XMPP_ENDPOINT) {
            this.userCredentials = {
                ...this.userCredentials,
                host: process.env.VUE_APP_XMPP_ENDPOINT
            };
        }
        // override XMPP domain if present in config file
        if (process.env.VUE_APP_XMPP_DOMAIN) {
            this.DOMAIN = process.env.VUE_APP_XMPP_DOMAIN;
        }
        // override XMPP receiver if present in config file
        if (process.env.VUE_APP_XMPP_RECEIVER) {
            this.RECEIVER = process.env.VUE_APP_XMPP_RECEIVER;
        }
        // setup isTypingBubbleTimeoutSecs
        if (process.env.VUE_APP_IS_TYPING_TIMEOUT_SECS) {
            this.isTypingBubbleTimeoutSecs = process.env.VUE_APP_IS_TYPING_TIMEOUT_SECS;
        }
        // setup showIsTypingBubbleSecs
        if (process.env.VUE_APP_SHOW_IS_TYPING_BUBBLE_SECS) {
            this.showIsTypingBubbleSecs = process.env.VUE_APP_SHOW_IS_TYPING_BUBBLE_SECS;
        }
        // setup messageTimeoutSecs
        if (process.env.VUE_APP_MESSAGE_TIMEOUT_SECS) {
            this.messageTimeoutSecs = process.env.VUE_APP_MESSAGE_TIMEOUT_SECS;
        }
        // setup shouldAllowLoginQueryParams
        if (process.env.VUE_APP_SHOULD_ALLOW_LOGIN_QUERY_PARAMS) {
            this.shouldAllowLoginQueryParams = process.env.VUE_APP_SHOULD_ALLOW_LOGIN_QUERY_PARAMS.toLocaleLowerCase() === "true";
        }
        // setup VUE_APP_XMPP_MESSAGE_PRIORITY
        if (process.env.VUE_APP_XMPP_MESSAGE_PRIORITY) {
            this.xmppMessagePriority = parseInt(process.env.VUE_APP_XMPP_MESSAGE_PRIORITY);
        }
        // setup VUE_APP_LAST_MESSAGE_TO_DISPLAY_TOTAL
        if (process.env.VUE_APP_LAST_MESSAGE_TO_DISPLAY_TOTAL) {
            this.lastMessageToDisplayTotal = parseInt(process.env.VUE_APP_LAST_MESSAGE_TO_DISPLAY_TOTAL);
        }
        // setup VUE_APP_SHOULD_AUTO_CLEAN_LS
        if (process.env.VUE_APP_SHOULD_AUTO_CLEAN_LS) {
            this.shouldAutoCleanLS = process.env.VUE_APP_SHOULD_AUTO_CLEAN_LS.toLocaleLowerCase() === "true";
        }
        // setup VUE_APP_AUTO_CLEAN_ATTEMPT_COUNT
        if (process.env.VUE_APP_AUTO_CLEAN_ATTEMPT_COUNT) {
            this.autoCleanAttemptCount = parseInt(process.env.VUE_APP_AUTO_CLEAN_ATTEMPT_COUNT);
        }
        // setup VUE_APP_GOOGLE_MAPS_API_LANG
        if (process.env.VUE_APP_GOOGLE_MAPS_API_LANG) {
            this.googleMapsApiLang = process.env.VUE_APP_GOOGLE_MAPS_API_LANG;
        }
        // setup debugMode
        if (process.env.VUE_APP_DEBUG) {
            this.debugMode = process.env.VUE_APP_DEBUG;
        }
    }
    setupConversationTree() {
        const localConversations = this.getLocalConversationsArray();
        const wfpChatReferenceIndex = localConversations.findIndex((conv) => conv.class === ConversationType.Wfp);
        // if Wfp's chat
        if (wfpChatReferenceIndex === -1) {
            localConversations.push(this._wfpChat);
        }
        this._webchatReference.setChatAvatarIcon(this._getRenderedAsset.avatar_wfp);
        // realign local storage
        this._slsSet(this._getLocalConversationTree, localConversations);
        // handle adjust spoc conversation messages
        this.handleAdjustSpocConversationMessage();
    }
    initAttachmentsUploader() {
        if (process.env.VUE_APP_ATTACHMENT_ENDPOINT) {
            this._webchatReference.setAttachmentsUploaderConfig({
                ...this._webchatReference.attachmentsUploaderConfig,
                actionPath: process.env.VUE_APP_ATTACHMENT_ENDPOINT
            });
        }
    }
    renderConversationsOrShowDedicatedChat() {
        const localConversations = this.getLocalConversationsArray();
        for (const conversation of localConversations) {
            const { class: currentClass } = conversation;
            if (currentClass && currentClass !== ConversationType.Deleted) {
                this._webchatReference.pushConversation(conversation);
            }
        }
        const matchIndex = localConversations.findIndex(({ class: currentClass }) => currentClass === ConversationType.Active);
        if (localConversations.length === 1) {
            const index = matchIndex !== -1 ? matchIndex : 0;
            this.setupConversationUI(localConversations[index]);
            this._webchatReference.setCurrentChat(localConversations[index]);
        }
        else {
            setTimeout(() => this._webchatReference.setCurrentChat({}), 3);
        }
        this._slsSet(this._getLocalConversationTree, localConversations);
        setTimeout(() => {
            this._webchatReference.$refs.conversationList.fetchHistoryMessages();
        }, 100);
    }
    onUpdateCurrentChatMessages(messages) {
        if (this.$refs.webchat && !!this.firstChatUpdateDone) {
            this.$refs.webchat.setChatHeaderTitle(this.chatHeaderTitle);
            this.$refs.webchat.setIsConversationHeaderRawHtml(false);
        }
        if (!this.firstChatUpdateDone) {
            this.firstChatUpdateDone = true;
        }
    }
    setupConversationUI(conversation) {
        this.firstChatUpdateDone = false;
        // update UI elements
        if (conversation.class !== ConversationType.Wfp) {
            // console.log('setupConversationUI', 'NOT SPOC MODE');
            this.chatHeaderTitle = conversation.title || strings.defaultChatTitle; // operator
            this._webchatReference.setChatHeaderTitle(this.chatHeaderTitle);
            this._webchatReference.setIsConversationHeaderRawHtml(false);
        }
        else {
            this.chatHeaderTitle = strings.defaultWfpTitle; // wfp bot
            // commented, bcos we disable privacy on the header
            // this._webchatReference.setChatHeaderTitle(this._getChatConversationHeaderTitle);
            // this._webchatReference.setIsConversationHeaderRawHtml(true);
            this._webchatReference.setChatHeaderTitle(this.chatHeaderTitle);
            this._webchatReference.setIsConversationHeaderRawHtml(false);
        }
        const chatBotMainIcon = conversation.class === ConversationType.Wfp
            ? this._getRenderedAsset.ico_wfp
            : this._getRenderedAsset.avatar_operatore;
        this._webchatReference.setChatHeaderIcon(chatBotMainIcon);
        // TODO: check on this
        this._webchatReference.toggleUserInputArea(false);
    }
    setupConnectionSessionInspector(waitFor = 1000) {
        this.sessionIntervalCycle = 0;
        this.sessionInterval = setInterval(() => {
            if (this.sessionIntervalCycle < this.sessionIntervalCyclesLimit) {
                this.sessionIntervalCycle++;
            }
            else {
                this.logout();
                console.log(`Reached maximum allowed time is ${this.sessionIntervalCyclesLimit}sec for this session. I'm logging out...`);
            }
        }, waitFor);
    }
    handleClearSessionInspector() {
        // clear webchatcore default session
        clearInterval(this._webchatReference.sessionInterval);
        this._webchatReference.sessionInterval = null;
        // since we only extend modification for webchatcore functionality
        // we need to clear our own session
        clearInterval(this.sessionInterval);
        this.sessionInterval = null;
    }
    handleFetchXmppCredsFromExternal(callback = (params) => { }) {
        if (this.shouldAllowLoginQueryParams) {
            // fetch creds from query string
            const { user, token, thread } = this.additionalURLParams;
            if (user && token) {
                const creds = {
                    user: `${user.split('@')[0]}.${token}`,
                    token,
                    thread: thread || this.getMainThread(),
                };
                // handle callback
                console.log("DEBUG: login parameters from query", { ...creds });
                callback({ ...creds });
            }
            else {
                console.warn("Missing parameters from query.");
            }
        }
        else {
            // fetch from postMessage
            window.addEventListener("message", (event) => {
                const safeSrcs = process.env.VUE_APP_ORIGIN_SAFE_SRCS;
                let safeSrcsArr = [];
                if (safeSrcs) {
                    safeSrcsArr = safeSrcs.toString().split(",");
                }
                // verify domain
                if (!safeSrcsArr.includes(event.origin)) {
                    console.warn("Message received from unauthorized source:");
                    return;
                }
                // Leggi i dati dal messaggio
                const data = event.data;
                if (data) {
                    // TODO: check on this, why with the fallback seems
                    // to add multiple open xmpp connections
                    // const { user, token, thread } = data || fakeXmppCreds;
                    let { user, token, thread } = data;
                    const creds = {
                        user: `${user.split('@')[0]}.${token}`,
                        token,
                        thread: thread || this.getMainThread(),
                    };
                    if (user && token) {
                        // handle callback
                        console.log("DEBUG: login parameters from postMessage", { ...creds });
                        callback({ ...creds });
                    }
                    else {
                        console.warn("Missing parameters from the received message");
                    }
                }
                else {
                    console.error("Missing parameters in the received message");
                }
            });
        }
    }
    getXmppLoginCreds() {
        const { user, token, thread } = this.xmppCreds;
        return {
            user,
            password: token,
            thread
        };
    }
    logout() {
        this._webchatReference.XMPPLogout();
        this.handleClearSessionInspector();
    }
    reLogin() {
        this.showLoader = true;
        this.loginAsUser();
        // clear all session first
        this.handleClearSessionInspector();
        // activate the session inspector
        this.setupConnectionSessionInspector();
        // clear loader
        this.showLoader = false;
    }
    loginAsUser() {
        const { user, password } = this.getXmppLoginCreds();
        this.userCredentials = {
            ...this.userCredentials,
            priority: this.xmppMessagePriority,
            user,
            password
        };
        try {
            console.log("DEBUG: XMPPLogin method call", this.userCredentials);
            this._webchatReference.XMPPLogin(this.userCredentials);
        }
        catch (error) {
            this._webchatReference.handleXMPPError(error);
        }
    }
    parseURLParams() {
        const uriKeysMap = { ver: "channelVersion" };
        const queryParams = getUrlQueryParams();
        for (const [key, value] of Object.entries(queryParams)) {
            if (this._allowedUriParams.includes(key)) {
                const payloadKey = uriKeysMap[key] || key;
                const urlParams = { ...this.additionalURLParams };
                this.additionalURLParams = {
                    ...urlParams,
                    [payloadKey]: value
                };
            }
        }
    }
    toggleIsChatConversationListVisited(toggle = false) {
        this.isChatConversationListVisited = toggle;
    }
    toggleIsPrivacyMessageClick(toggle = false) {
        this.isPrivacyMessageClick = toggle;
    }
    handleInputModesBeforeAfter(msg = {}) {
        let msgItem = msg;
        if (Object.keys(msg).length <= 0) {
            // get the last item of the getCurrentChatMessages
            const [lastMsgItem] = this._webchatReference.getCurrentChatMessages.slice(-1);
            msgItem = lastMsgItem || {};
        }
        const { payload } = msgItem;
        if (payload) {
            const { inputData } = payload;
            if (inputData && inputData.choice) {
                const { modeBeforeSubmit, visibilityAfterSubmit } = inputData.choice;
                // console.log("modeBeforeSubmit: ", modeBeforeSubmit);
                // console.log("visibilityAfterSubmit: ", visibilityAfterSubmit);
                this._webchatReference.setModeBeforeSubmit(modeBeforeSubmit);
                this._webchatReference.setVisibilityAfterSubmit(visibilityAfterSubmit);
            }
            else {
                // reset
                this._webchatReference.setModeBeforeSubmit("");
                this._webchatReference.setVisibilityAfterSubmit("");
            }
        }
    }
    handleWelcomeMessage(force = false, storeInLs = false) {
        const currentChatMessages = this._webchatReference.getCurrentChatMessages;
        const isConversationsList = this._webchatReference.canShowConversationsView;
        if ((!isConversationsList && currentChatMessages.length <= 0) || force) {
            const welcomeMessage = this.getSpocWelcomeMessage();
            this._webchatReference.storeMessage(welcomeMessage, storeInLs);
        }
    }
    handleTimeoutMessage(force = false) {
        const currentChatMessages = this._webchatReference.getCurrentChatMessages;
        const isConversationsList = this._webchatReference.canShowConversationsView;
        if ((!isConversationsList && !currentChatMessages.length) || force) {
            const timeoutMessage = this.getSetupXMPPMessageEnvelope({
                uuid: _generateUUID(),
                thread: this.getMainThread(),
                is_premium_message: false,
                raw_payload: { ...timeoutMessagePayload },
                subject: "text",
                type: "chat_text",
                action: "chat_text",
                direction: "out"
            });
            this._webchatReference.storeMessage(timeoutMessage, false);
        }
    }
    handleRestartMessage() {
        const restartChatMessage = this.getSetupXMPPMessageEnvelope({
            uuid: _generateUUID(),
            thread: this.getMainThread(),
            is_premium_message: false,
            raw_payload: { ...restartMessagePayload },
            subject: "text",
            type: "chat_text",
            action: "chat_text",
            direction: "out"
        });
        this._webchatReference.sendMessageAction(restartChatMessage);
        // reset location
        this._webchatReference.setIsInteractionInsertLocation(false);
        // reset date node
        this._webchatReference.setIsInteractionInsertDate(false);
        this._webchatReference.setCurrentDatePickerInfo({});
    }
    handleLastConversationTimeoutMessage(msg, lastMsgTimestamp) {
        console.log("timeout lastMsgTimestamp: ", lastMsgTimestamp);
        // trigger timeout message to all nodes
        // except if a node is a restart node
        // or a node is a final node
        const isMessageRestartNode = this._webchatReference.isMessageRestartNode(msg);
        const isMessageFinalNode = this._webchatReference.isMessageFinalNode(msg);
        console.log("isRestartNode: ", isMessageRestartNode);
        console.log("isFinalNode: ", isMessageFinalNode);
        if (!(isMessageRestartNode || isMessageFinalNode)) {
            this.handleTimeoutMessage(true);
            // reset location node, if present
            this._webchatReference.setIsInteractionInsertLocation(false);
            this._webchatReference.setIsLocationModalEnable(false);
            // reset date node, if present
            this._webchatReference.setIsInteractionInsertDate(false);
            this._webchatReference.setCurrentDatePickerInfo({});
            // TODO: are there other possible resets too? find out...
        }
    }
    // TODO: remove when carousel message incoming is working
    handleCarouselMessage(force = false) {
        const currentChatMessages = this._webchatReference.getCurrentChatMessages;
        const isConversationsList = this._webchatReference.canShowConversationsView;
        if ((!isConversationsList && !currentChatMessages.length) || force) {
            const carouselMessage = this.getSetupXMPPMessageEnvelope({
                uuid: _generateUUID(),
                thread: this.getMainThread(),
                raw_payload: { ...carouselMessageSingleChoiceImageText },
                is_premium_message: false
            });
            this._webchatReference.storeMessage(carouselMessage, false);
        }
    }
    onAfterXmppQueryArchive() {
        setTimeout(() => {
            // handle welcome message
            this.handleWelcomeMessage();
            // get main thread
            const mainThread = this.getMainThread();
            // reset unread messagges
            this._webchatReference.handleFireResetUnreadMessages(mainThread);
            // console.log("onAfterXmppQueryArchive callback...");
            // do something only if the confirmation popup
            // is still not confirm
            if (this.showVerificationPopup) {
                // we need to disable the chat area
                // as we want them to click the verification popup first 
                this._webchatReference.toggleUserInputStatus(false);
            }
            // TODO: remove when working
            // this.handleCarouselMessage(true);
            // console.log("localMessagesTree: ", JSON.stringify(localMessagesTree));
        }, 300);
    }
    onAfterXmppQueryArchivePagination() {
        setTimeout(() => {
            // console.log("onAfterXmppQueryArchivePagination callback...");
            // TODO: remove when working
            // this.handleCarouselMessage(true);
            // const localMessagesTree = this.getLocalMessagesDictionary();
            // console.log("localMessagesTree: ", JSON.stringify(localMessagesTree));
        }, 300);
    }
    onConfirmVerificationPopup() {
        // disable popup
        this.showVerificationPopup = false;
        // enable chat area
        this._webchatReference.toggleUserInputStatus(true);
    }
    onDeclineVerificationPopup() {
        this.showVerificationPopupDeclined = true;
    }
    onAttachmentError(error) {
        const errorsMap = new Map([
            ["1", strings.attachmentsErrorNotFound],
            ["2", strings.attachmentsErrorExtensionNotAllowed],
            ["3", strings.attachmentsErrorFileTooBig],
            ["1001", strings.attachmentsErrorNotFound],
            ["1002", strings.attachmentsErrorExtensionNotAllowed],
            ["1003", strings.attachmentsErrorFileTooBig],
            ["1101", strings.attachmentsErrorInvalidConversation]
        ]);
        const defaultMsg = strings.attachmentsErrorDefault;
        return error.code ? errorsMap.get("" + error.code) || defaultMsg : defaultMsg;
    }
    renderAsset(imagePath) {
        // @ts-ignore
        return require(`@/assets/${THEME_PATH}/images/${imagePath}`);
    }
    getLocalMessagesDictionary() {
        return this._slsGet(this._getLocalMessageTree) || {};
    }
    getLocalConversationsArray(fetchFromArchive = false) {
        const conversations = this._slsGet(this._getLocalConversationTree, "is_array");
        const conversationArray = fetchFromArchive
            ? conversations.filter((c) => c.is_archived)
            : conversations;
        return conversationArray.sort((c1, c2) => {
            return (c2.pin_date || 0) - (c1.pin_date || 0);
        });
    }
    async emitCloseChatEvent() {
        const code = noticeCodes[4];
        const message = {
            type: "chat_notice",
            action: "chat_notice",
            payload: { code }
        };
        this._webchatReference.sendXMPPMessage(message);
        this._webchatReference.storeMessage(message);
        this.showCloseChatCta = false;
        this.showDialog = false;
        this._webchatReference.updateConversationClass({
            thread: this._webchatReference.currentChat.thread,
            newClass: ConversationType.LocalClosed
        });
        await this._webchatReference.setCurrentChat({});
        setTimeout(async () => {
            await this._webchatReference.$refs.conversationList.renderActiveConversations();
        }, 100);
    }
    emitArchiveChatEvent() {
        // create closing message
        const message = {
            type: "chat_notice",
            action: "chat_notice",
            payload: { code: noticeCodes[4] }
        };
        // send closing message
        this._webchatReference.sendMessageAction(message);
        // update UI
        const conversation = this._webchatReference.currentChat;
        const localConversationsTree = this._slsGet(this._getLocalConversationTree) || [];
        const index = localConversationsTree.findIndex((c) => c.thread === conversation.thread);
        if (index !== -1) {
            localConversationsTree[index].class = ConversationType.Archived;
            localConversationsTree[index].is_archived = true;
            this._slsSet(this._getLocalConversationTree, localConversationsTree);
            this._webchatReference.updateConversationClass({
                thread: conversation.thread,
                newClass: ConversationType.Archived
            });
        }
        this._webchatReference.$refs.conversationList.confirmArchiveAction(conversation);
        this.showCloseChatCta = false;
        this.showDialog = false;
        // go back to conversations list
        this._webchatReference.setCurrentChat({});
    }
    extractTimestampFromMamMessage(message) {
        if (!message || !message.stanza || !message.stanza.rawStanza) {
            return false;
        }
        const parser = new DOMParser();
        const xmlMsg = parser.parseFromString(message.stanza.rawStanza, "text/xml");
        let parsedXMLStanza = document.createElement("div");
        if (xmlMsg.firstElementChild &&
            xmlMsg.firstElementChild.firstElementChild &&
            xmlMsg.firstElementChild.firstElementChild.firstElementChild &&
            xmlMsg.firstElementChild.firstElementChild.firstElementChild.firstElementChild) {
            parsedXMLStanza = xmlMsg.firstElementChild.firstElementChild.firstElementChild.firstElementChild;
        }
        return parsedXMLStanza.getAttribute("stamp");
    }
    handleShowIsTypingBubble(toggle = false) {
        if (typeof this.isTypingBubbleTimeoutId === "number" && !toggle) {
            clearTimeout(this.isTypingBubbleTimeoutId);
            this.isTypingBubbleTimeoutId = undefined;
            this._webchatReference.showIsTypingBubble = false;
            this.showIsTypingBubble = false;
            // enable chat input
            // this._webchatReference.toggleUserInputArea(true);
            this._webchatReference.toggleUserInputStatus(true);
        }
        if (toggle) {
            this._webchatReference.showIsTypingBubble = true;
            this.showIsTypingBubble = true;
            // disable chat input
            // this._webchatReference.toggleUserInputArea(false);
            this._webchatReference.toggleUserInputStatus(false);
            const isTypingTimeout = this.isTypingBubbleTimeoutSecs * 1000;
            this.isTypingBubbleTimeoutId = setTimeout(() => {
                this._webchatReference.showIsTypingBubble = false;
                this.showIsTypingBubble = false;
                // enable chat input
                // this._webchatReference.toggleUserInputArea(true);
                this._webchatReference.toggleUserInputStatus(true);
            }, isTypingTimeout);
        }
    }
};
__decorate([
    Action("setXmppCreds")
], App.prototype, "setXmppCreds", void 0);
__decorate([
    Getter("xmppCreds")
], App.prototype, "xmppCreds", void 0);
App = __decorate([
    Component({
        components: {
            WfpChat,
            CallToAction
        }
    })
], App);
export default App;
